import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { formatDate } from '@tallyframe/core';
import { brandStyle, resolveBrand } from './brand.js';
/** Logo and operator name on the left, "Prepared for <recipient>" on the
 *  right. A logo that fails to load is removed rather than left as a broken
 *  image icon; the operator name still identifies the report. The failure is
 *  keyed to the URL that failed, not a bare flag, so a later `brand` update
 *  carrying a corrected `logoUrl` shows the image again instead of staying
 *  hidden forever. */
export function BrandLine(props) {
    const { brand, preparedFor, preparedBy } = props;
    const [failedUrl, setFailedUrl] = useState(null);
    const showLogo = Boolean(brand.logoUrl) && brand.logoUrl !== failedUrl;
    return (_jsxs("div", { className: "tf-brand-line", children: [_jsxs("div", { className: "tf-brand-identity", children: [showLogo ? (_jsx("img", { className: "tf-brand-logo", src: brand.logoUrl ?? undefined, alt: brand.name, onError: () => setFailedUrl(brand.logoUrl) })) : null, brand.name ? _jsx("span", { className: "tf-brand-name", children: brand.name }) : null] }), preparedBy ? (
            // The right column: Prepared for first, each line only when set. With
            // no Prepared by the lone Prepared for stays a direct child, exactly
            // as it was before theming.
            _jsxs("div", { className: "tf-brand-prepared", children: [preparedFor ? (_jsxs("div", { className: "tf-brand-prepared-for", children: ["Prepared for ", _jsx("strong", { children: preparedFor })] })) : null, _jsxs("div", { className: "tf-brand-prepared-by", children: ["Prepared by ", _jsx("strong", { children: preparedBy })] })] })) : preparedFor ? (_jsxs("div", { className: "tf-brand-prepared-for", children: ["Prepared for ", _jsx("strong", { children: preparedFor })] })) : null] }));
}
export function ReportHeader(props) {
    const { title, subtitle, preparedFor, dataAsOf, selectable, selected, onSelect } = props;
    const resolved = useMemo(() => resolveBrand(props.brand), [props.brand?.accent, props.brand?.name, props.brand?.logoUrl]);
    const handleClick = () => {
        if (!selectable)
            return;
        onSelect?.();
    };
    const handleKeyDown = (event) => {
        if (!selectable)
            return;
        if (event.target !== event.currentTarget)
            return;
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            onSelect?.();
        }
    };
    const className = [
        'tf-report-header',
        selectable ? 'tf-report-header--selectable' : null,
        selectable && selected ? 'tf-report-header--selected' : null
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("header", { className: className, style: brandStyle(resolved), 
        // `group`, not the `<header>` element's implicit `banner` landmark:
        // this component is mounted by hosts (apps/web's AppShell, the
        // Workspace preview, the PDF cover) that may already have their own
        // top-level banner, and a nested banner is invalid per HTML-AAM —
        // axe flags it as `landmark-banner-is-top-level`. `group` still takes
        // an accessible name without claiming a landmark role.
        role: selectable ? 'group' : undefined, tabIndex: selectable ? 0 : undefined, "aria-label": selectable ? `${title} header` : undefined, "aria-current": selected ? 'true' : undefined, onClick: selectable ? handleClick : undefined, onKeyDown: selectable ? handleKeyDown : undefined, children: [_jsx("div", { className: "tf-header-bar" }), _jsx("div", { className: "tf-header-top", children: _jsx(BrandLine, { brand: resolved, preparedFor: preparedFor, preparedBy: props.brand?.theme?.preparedBy || undefined }) }), _jsx("h1", { className: "tf-header-title", children: title }), subtitle ? _jsx("p", { className: "tf-header-subtitle", children: subtitle }) : null, dataAsOf ? (_jsxs("p", { className: "tf-header-meta", children: ["Data as of ", _jsx("strong", { children: formatDate(dataAsOf) })] })) : null] }));
}
//# sourceMappingURL=ReportHeader.js.map